import { useEffect, useRef, useState } from "react";
import { Modal } from "@tw/tw-common-js";
import moment from "moment-timezone";
import api from "../../services/api";
import { useDictionary } from "../../context/useDictionary";
import { TIMEZONES_UTC } from "../../services/timezones";

export default function EmployeViewModal({ employee: initialEmployee, hrCode, onClose }) {
  const { getDictionaryValue } = useDictionary();

  const [employee, setEmployee] = useState(initialEmployee || null);
  const [loading, setLoading] = useState(!initialEmployee);
  const [supervisorName, setSupervisorName] = useState(null);
  const [advisorName, setAdvisorName] = useState(null);
  const [profileImgUrl, setProfileImgUrl] = useState(null);

  // Live clock — updates every minute
  const [currentTime, setCurrentTime] = useState(Date.now());
  const timerRef = useRef();
  useEffect(() => {
    timerRef.current = setInterval(() => setCurrentTime(Date.now()), 60000);
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (!employee && hrCode) {
      setLoading(true);
      api.getEmployeeByHrCode(hrCode).then((res) => {
        if (res.status === 200) setEmployee(res.data);
        setLoading(false);
      });
    }
  }, [hrCode, employee]);

  // Load profile image if present
  useEffect(() => {
    if (!employee?.profileImg || !employee?.hrCode) return;
    api.getProfileImgThumb(employee.hrCode).then((res) => {
      if (res.status === 200 && res.data) {
        setProfileImgUrl(URL.createObjectURL(res.data));
      }
    });
  }, [employee?.hrCode, employee?.profileImg]);

  // Resolve supervisor/advisor names from hrCodes
  useEffect(() => {
    if (!employee) return;
    if (employee._paySupervisor) {
      api.getEmployeeByHrCode(employee._paySupervisor).then((res) => {
        if (res.status === 200 && res.data) {
          setSupervisorName(`${res.data.firstName} ${res.data.lastName}`);
        }
      });
    }
    if (employee._payAdvisor) {
      api.getEmployeeByHrCode(employee._payAdvisor).then((res) => {
        if (res.status === 200 && res.data) {
          setAdvisorName(`${res.data.firstName} ${res.data.lastName}`);
        }
      });
    }
  }, [employee]);

  const fullName = employee ? `${employee.firstName || ""} ${employee.lastName || ""}`.trim() : hrCode;

  // Build timezone display: "UTC-4 (EDT) - 12:20 pm"
  const timezoneDisplay = (() => {
    if (!employee?.timezone) return null;
    const tzObj = TIMEZONES_UTC.find((tz) => tz.timezone === employee.timezone);
    if (!tzObj) return null;
    const tzTime = moment(currentTime).tz(tzObj.timezone).format("hh:mm A");
    const abbr = moment.tz(tzObj.timezone).zoneAbbr();
    return `${tzObj.label} (${abbr}) - ${tzTime}`;
  })();

  const subheader = [
    employee?.hrCode,
    employee?.Team?.name,
    employee?.Team?.Department?.name,
    employee?.Team?.Department?.Division?.name,
    employee?.country,
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <Modal close={onClose} size="medium_2 vertical-medium" title="Teammate Profile">
      {loading ? (
        <div className="employe-view-loading">Loading...</div>
      ) : !employee ? (
        <div className="employe-view-loading">Employee not found.</div>
      ) : (
        <>
          {/* Profile header */}
          <div className="employe-view-modal-profile">
            <div
              className="employe-view-modal-avatar"
              style={profileImgUrl ? { padding: 0, overflow: "hidden", backgroundColor: "transparent" } : {}}
            >
              {profileImgUrl ? (
                <img
                  src={profileImgUrl}
                  alt={fullName}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", borderRadius: "50%" }}
                />
              ) : (
                <>
                  {employee?.firstName?.charAt(0)}
                  {employee?.lastName?.charAt(0)}
                </>
              )}
            </div>
            <div className="employe-view-modal-info">
              <div className="employe-view-modal-name">{fullName}</div>
              {subheader && <div className="employe-view-modal-subheader">{subheader}</div>}
              {employee.email && <div className="employe-view-modal-meta">{employee.email}</div>}
              {timezoneDisplay && <div className="employe-view-modal-meta">{timezoneDisplay}</div>}
            </div>
          </div>

          {/* Demographics */}
          <div className="subtitle">Demographics</div>
          <div className="employe-view-fields">
            {[
              { label: "First Name", value: employee.firstName },
              { label: "Last Name", value: employee.lastName },
              { label: "TW Email", value: employee.email },
              { label: "Country", value: employee.country },
              { label: "Position", value: employee.position || getDictionaryValue("Positions", employee._position) },
              { label: "Team", value: employee.Team?.name },
              { label: "Department", value: employee.Team?.Department?.name },
              { label: "Division", value: employee.Team?.Department?.Division?.name },
              { label: "TW Office", value: employee.twOffice || getDictionaryValue("TW Offices", employee._twOffice) },
            ].map(({ label, value }) => (
              <div className="label-row" key={label}>
                <label>{label}</label>
                <input value={value || ""} disabled readOnly />
              </div>
            ))}
          </div>
          {/* Performance Details */}
          <div className="subtitle">Performance Details</div>
          <div className="employe-view-fields">
            {[
              { label: "Performance Supervisor", value: supervisorName || employee._paySupervisor },
              { label: "Performance Advisor", value: advisorName || employee._payAdvisor },
            ].map(({ label, value }) => (
              <div className="label-row" key={label}>
                <label>{label}</label>
                <input value={value || ""} disabled readOnly />
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}
